import React from 'react';
import { View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Body, Caption, SubHeading } from '@/components/ui/Text';

type StorageUsageCardProps = {
  totalSize: number;
  breakdown: {
    maps: number;
    documents: number;
    itinerary: number;
    safety: number;
  };
  formatSize?: (bytes: number) => string;
};

export function StorageUsageCard({
  totalSize,
  breakdown,
  formatSize,
}: StorageUsageCardProps) {
  const theme = useTheme();

  const formatStorageSize = (bytes: number): string => {
    if (formatSize) {
      return formatSize(bytes);
    }
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  const getPercentage = (size: number): number => {
    if (totalSize === 0) return 0;
    return (size / totalSize) * 100;
  };

  const sections = [
    { name: 'Maps', size: breakdown.maps, color: theme.colors.primary, icon: 'map-outline' as const },
    { name: 'Documents', size: breakdown.documents, color: '#7C3AED', icon: 'document-outline' as const },
    { name: 'Itinerary', size: breakdown.itinerary, color: '#059669', icon: 'calendar-outline' as const },
    { name: 'Safety', size: breakdown.safety, color: '#DC2626', icon: 'shield-outline' as const },
  ].filter(section => section.size > 0);

  return (
    <Card variant="outlined">
      <View style={{ gap: theme.spacing(2) }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
          <SubHeading style={{ fontSize: 18 }}>Storage Usage</SubHeading>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
            <Ionicons name="folder-outline" size={20} color={theme.colors.primary} />
            <Body style={{ fontWeight: '600', fontSize: 16 }}>
              {formatStorageSize(totalSize)}
            </Body>
          </View>
        </View>

        {sections.length > 0 ? (
          <View style={{ gap: theme.spacing(1.5) }}>
            {sections.map((section) => {
              const percentage = getPercentage(section.size);
              return (
                <View key={section.name} style={{ gap: theme.spacing(0.5) }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                      <Ionicons name={section.icon} size={16} color={section.color} />
                      <Caption color={theme.colors.textSecondary}>{section.name}</Caption>
                    </View>
                    <Caption color={theme.colors.textSecondary} style={{ fontWeight: '600' }}>
                      {formatStorageSize(section.size)} ({percentage.toFixed(1)}%)
                    </Caption>
                  </View>
                  <View
                    style={{
                      height: 6,
                      backgroundColor: theme.colors.borderMuted,
                      borderRadius: theme.radii.sm,
                      overflow: 'hidden',
                    }}
                  >
                    <View
                      style={{
                        height: '100%',
                        width: `${percentage}%`,
                        backgroundColor: section.color,
                        borderRadius: theme.radii.sm,
                      }}
                    />
                  </View>
                </View>
              );
            })}
          </View>
        ) : (
          <View style={{ alignItems: 'center', paddingVertical: theme.spacing(2) }}>
            <Ionicons name="cloud-outline" size={32} color={theme.colors.textSecondary} />
            <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(1) }}>
              No offline content stored
            </Caption>
          </View>
        )}
      </View>
    </Card>
  );
}






